import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Megaphone, TrendingUp, Church, MapPin } from "lucide-react";
import { AdCreditBalance, useAdCredits, toastCreditError } from "@/components/AdCreditBalance";
import { fmtMoney } from "@/lib/payments";

const DURATIONS = [3, 7, 14, 30];

// Boost a post: the budget is debited from the member's ad-credit wallet (1.5.2 — no Stripe checkout).
export const PromoteModal = ({ postId, open, onOpenChange, onPromoted }) => {
  const { config, user, t, lang } = useApp();
  const nav = useNavigate();
  const [budget, setBudget] = useState(20);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [churches, setChurches] = useState([]);
  const [mode, setMode] = useState("promote"); // "promote" | "community"
  const [churchId, setChurchId] = useState("");
  const { data: credits, balance, currency, reload } = useAdCredits(open && !!user);

  const feed = config?.settings?.algorithms?.feed || {};
  const commPrice = Number(feed.community_event_price ?? 5);
  const commDays = Number(feed.community_event_days ?? 7);
  const cur = config?.payments?.currency || config?.settings?.default_currency || "EUR";
  const isSuper = user?.role === "super_admin";
  const isStaff = ["super_admin", "manager"].includes(user?.role);
  const free = mode === "community" ? isStaff : isSuper;
  const need = mode === "community" ? commPrice : Number(budget) || 0;

  useEffect(() => {
    if (!open) return;
    setMode("promote");
    api.get("/churches/mine").then(({ data }) => {
      const list = data.churches || [];
      setChurches(list);
      if (list.length) setChurchId(list[0].id);
    }).catch(() => setChurches([]));
  }, [open]);

  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "community") {
        const { data } = await api.post(`/posts/${postId}/boost-community`, { church_id: churchId });
        toast.success(t("promote.community_done", "Événement boosté localement{city} au tarif communauté 🎉").replace("{city}", data.city ? ` (${data.city})` : ""));
      } else {
        await api.post(`/posts/${postId}/promote`, { budget: Number(budget) || 0, days });
        toast.success(t("promote.done", "Publication promue ! Elle apparaît maintenant comme sponsorisée 🎉"));
      }
      reload();
      onPromoted?.();
      onOpenChange(false);
    } catch (e) {
      toastCreditError(e, t, nav, t("promote.error", "Erreur lors de la promotion"));
    } finally {
      setLoading(false);
    }
  };

  const estReach = Math.round((Number(budget) || 0) * 120 * days / 7);
  const selectedChurch = churches.find((c) => c.id === churchId);
  const short = !free && need > balance;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" className="sm:max-w-md" data-testid="promote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Megaphone size={18} /></span>
            {t("promote.title", "Promouvoir la publication")}
          </DialogTitle>
          <DialogDescription>{t("promote.desc", "Transforme ta publication en publicité sponsorisée visible dans le fil des membres.")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">

        {churches.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode("promote")}
              className="rounded-xl px-3 py-2.5 text-sm font-semibold border transition-colors"
              style={mode === "promote"
                ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" }
                : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)", borderColor: "var(--yalola-border)" }}
              data-testid="promote-mode-standard"
            >
              {t("promote.mode_standard", "Publicité standard")}
            </button>
            <button
              onClick={() => setMode("community")}
              className="rounded-xl px-3 py-2.5 text-sm font-semibold border flex items-center justify-center gap-1 transition-colors"
              style={mode === "community"
                ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" }
                : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)", borderColor: "var(--yalola-border)" }}
              data-testid="promote-mode-community"
            >
              <Church size={15} /> {t("promote.mode_community", "Événement communauté")}
            </button>
          </div>
        )}

        {mode === "community" ? (
          <div className="space-y-4">
            {churches.length > 1 && (
              <div>
                <Label>{t("promote.church", "Église")}</Label>
                <select
                  value={churchId}
                  onChange={(e) => setChurchId(e.target.value)}
                  className="w-full mt-1 rounded-xl border px-3 py-2 text-sm bg-transparent"
                  style={{ borderColor: "var(--yalola-border)" }}
                  data-testid="promote-church-select"
                >
                  {churches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="rounded-xl px-4 py-3 flex items-start gap-2 text-sm" style={{ background: "var(--yalola-secondary-soft)", color: "var(--yalola-secondary)" }}>
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>{t("promote.community_hint", "Diffusion locale prioritaire{city} pendant {days} jours — vos membres et voisins voient l'événement en premier.").replace("{city}", selectedChurch?.city ? ` ${t("promote.in_city", "à")} ${selectedChurch.city}` : "").replace("{days}", String(commDays))}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
              <span className="text-sm font-medium">{t("promote.community_rate", "Tarif communauté")}</span>
              <span className="font-extrabold text-lg" style={{ color: "var(--yalola-primary)" }} data-testid="promote-community-price">{fmtMoney(commPrice, cur, lang)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>{t("promote.budget", "Budget ({cur})").replace("{cur}", cur)}</Label>
              <Input type="number" min="1" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="promote-budget" />
            </div>
            <div>
              <Label>{t("promote.duration", "Durée")}</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDays(d)}
                    className="pill justify-center text-sm"
                    style={days === d
                      ? { background: "var(--yalola-primary)", color: "#fff" }
                      : { background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
                    data-testid={`promote-days-${d}`}
                  >
                    {d} {t("ads.days_short", "j")}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl px-4 py-3 flex items-center gap-2 text-sm" style={{ background: "var(--yalola-secondary-soft)", color: "var(--yalola-secondary)" }}>
              <TrendingUp size={16} /> {t("promote.reach", "Portée estimée :")} <b>~{estReach.toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</b> {t("promote.people", "personnes")}
            </div>
          </div>
        )}

        {!free && (
          <AdCreditBalance balance={balance} currency={currency || cur} need={need} loading={!credits} onBeforeNavigate={() => onOpenChange(false)} />
        )}
        </DialogBody>
        <DialogFooter>
          {mode === "community" ? (
            <Button className="pill pill-primary w-full justify-center" onClick={submit} disabled={loading || !churchId || short} data-testid="promote-submit-community">
              {loading ? t("promote.boosting", "Boost en cours...") : `${t("promote.boost_event", "Booster l'événement")} (${fmtMoney(commPrice, cur, lang)})`}
            </Button>
          ) : (
            <Button className="pill pill-primary w-full justify-center" onClick={submit} disabled={loading || short} data-testid="promote-submit">
              {loading ? t("promote.loading", "Promotion...") : free ? t("promote.submit", "Promouvoir maintenant") : `${t("promote.submit", "Promouvoir maintenant")} · ${fmtMoney(need, cur, lang)}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
